import { useEffect, useState } from 'react';
import { SceneStore, type WorldBox } from '@os/doc';
import { obbBounds, unionRects } from '@os/engine';
import { EmbedContext, ExportModeContext } from '../editor/embeds';
import type { MediaResolver } from '../editor/lod';
import { useChildIds } from '../editor/useObject';
import { ObjectView } from '../objects/ObjectView';
import { MiniatureDepth } from './miniature';

const noRegister = () => undefined;
const PAD = 16;
const bounds = (f: WorldBox) => obbBounds({ cx: f.x + f.w / 2, cy: f.y + f.h / 2, w: f.w, h: f.h, rotation: f.rotation });

/**
 * Another page drawn live, read-only and scaled to `w × h` (P14 Ruling 12): a second store on the same document
 * shows that page, so each of its objects re-renders only when it changes, with the canvas's own components.
 * Embeds stay previews (export mode) and nothing inside takes the pointer.
 */
export function PageMiniature({ store, pageId, media, w, h, depth }: { store: SceneStore; pageId: string; media: MediaResolver; w: number; h: number; depth: number }) {
  const [mirror, setMirror] = useState<SceneStore | null>(null);
  useEffect(() => {
    const m = new SceneStore(store.doc);
    m.setReadOnly(true);
    m.setPage(pageId);
    setMirror(m);
    return () => m.destroy();
  }, [store.doc, pageId]);
  return mirror ? <Scene mirror={mirror} media={media} w={w} h={h} depth={depth} /> : null;
}

function Scene({ mirror, media, w, h, depth }: { mirror: SceneStore; media: MediaResolver; w: number; h: number; depth: number }) {
  const roots = useChildIds(mirror, null);
  const g = mirror.graph();
  const box = unionRects(roots.flatMap((id) => (g.world.has(id) && !g.hidden.has(id) ? [bounds(g.world.get(id)!)] : [])));
  if (!box) return null;
  const bw = box.maxX - box.minX + 2 * PAD;
  const bh = box.maxY - box.minY + 2 * PAD;
  const k = Math.min(w / bw, h / bh);
  const ox = (w - bw * k) / 2 - (box.minX - PAD) * k;
  const oy = (h - bh * k) / 2 - (box.minY - PAD) * k;
  return (
    <div data-page-miniature={mirror.page} aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute left-0 top-0 origin-top-left" style={{ transform: `matrix(${k}, 0, 0, ${k}, ${ox}, ${oy})` }}>
        <MiniatureDepth.Provider value={depth + 1}>
          <ExportModeContext.Provider value={true}>
            <EmbedContext.Provider value={null}>
              {roots.map((id) => (
                <ObjectView key={id} store={mirror} id={id} media={media} register={noRegister} />
              ))}
            </EmbedContext.Provider>
          </ExportModeContext.Provider>
        </MiniatureDepth.Provider>
      </div>
    </div>
  );
}
